import { useEffect, useState } from "react";

/**
 * 메모 작성/편집 모달 컴포넌트
 * @param {Object} props
 * @param {boolean} props.isOpen - 모달 표시 여부
 * @param {number} props.id - 식별자 ID
 * @param {string|null} props.currentMemo - 저장된 메모. null이면 신규 작성 모드
 * @param {boolean} props.loading - 저장/삭제 중 여부
 * @param {Function} props.onSave - 저장 버튼 클릭 시 호출 (memoText: string)
 * @param {Function} props.onDelete - 삭제 버튼 클릭 시 호출
 * @param {Function} props.onClose - 닫기/취소 버튼 및 ESC 키 클릭 시 호출
 */
function MemoModal({
  isOpen,
  id,
  currentMemo,
  loading,
  onSave,
  onDelete,
  onClose,
}) {
  const [memoText, setMemoText] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMemoText(currentMemo || "");
      setConfirmingDelete(false);
    }
  }, [isOpen, currentMemo]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        if (confirmingDelete) {
          setConfirmingDelete(false);
        } else {
          onClose();
        }
      }
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, confirmingDelete]);

  const canSave = memoText.trim().length > 0 && !loading;

  if (!isOpen) return null;

  const btnBase = {
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
  };

  return (
    <div
      className="modal-overlay"
      data-testid="modal-overlay"
      onClick={onClose}
    >
      <div
        className="modal-content memo-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "460px",
          width: "90%",
          padding: "0",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: "18px 20px 16px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <span
              style={{ fontSize: "16px", fontWeight: 600, color: "#1f2937" }}
            >
              메모
            </span>
            {id && (
              <span
                style={{
                  marginLeft: "8px",
                  fontSize: "12px",
                  color: "#9ca3af",
                }}
              >
                #{id}
              </span>
            )}
          </div>
          <button
            className="modal-close-btn"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "18px",
              cursor: "pointer",
              color: "#9ca3af",
              padding: "2px 6px",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {confirmingDelete ? (
          /* 삭제 확인 화면 */
          <div style={{ padding: "32px 28px 28px", textAlign: "center" }}>
            <div style={{
              width: "48px", height: "48px", borderRadius: "50%",
              backgroundColor: "#fff1f2", margin: "0 auto 16px",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "20px",
            }}>
              🗑️
            </div>
            <p style={{ margin: "0 0 8px", fontSize: "16px", fontWeight: 700, color: "#111827" }}>
              메모를 삭제할까요?
            </p>
            <p style={{ margin: "0 0 28px", fontSize: "13px", color: "#9ca3af", lineHeight: 1.6 }}>
              삭제된 메모는 복구할 수 없습니다.
            </p>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setConfirmingDelete(false)}
                disabled={loading}
                style={{ ...btnBase, flex: 1, padding: "11px 0", backgroundColor: "#f3f4f6", color: "#374151", border: "none", fontSize: "14px" }}
              >
                취소
              </button>
              <button
                onClick={onDelete}
                disabled={loading}
                style={{ ...btnBase, flex: 1, padding: "11px 0", backgroundColor: loading ? "#fca5a5" : "#ef4444", color: "white", border: "none", fontSize: "14px", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? "삭제 중..." : "삭제"}
              </button>
            </div>
          </div>
        ) : (
          /* 편집 화면 */
          <>
            <div style={{ padding: "20px" }}>
              <textarea
                value={memoText}
                onChange={(e) => setMemoText(e.target.value)}
                placeholder="메모를 입력하세요"
                autoFocus
                style={{
                  width: "100%",
                  minHeight: "130px",
                  padding: "12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  resize: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div
              style={{
                padding: "0 20px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {currentMemo ? (
                <button
                  onClick={() => setConfirmingDelete(true)}
                  disabled={loading}
                  style={{
                    ...btnBase,
                    padding: "9px 16px",
                    backgroundColor: "white",
                    color: "#dc2626",
                    border: "1px solid #fca5a5",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  삭제
                </button>
              ) : (
                <div />
              )}

              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={onClose}
                  disabled={loading}
                  style={{
                    ...btnBase,
                    padding: "9px 16px",
                    backgroundColor: "white",
                    color: "#6b7280",
                    border: "1px solid #e5e7eb",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  취소
                </button>
                <button
                  onClick={() => onSave(memoText)}
                  disabled={!canSave}
                  style={{
                    ...btnBase,
                    padding: "9px 20px",
                    backgroundColor: canSave ? "#6366f1" : "#e5e7eb",
                    color: canSave ? "white" : "#9ca3af",
                    fontWeight: 600,
                    cursor: canSave ? "pointer" : "not-allowed",
                  }}
                >
                  {loading ? "저장 중..." : "저장"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MemoModal;
